import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useHistory } from "react-router-dom";
import { TableCell, TableRow, Typography, Tooltip } from "@mui/material";
import { Add } from "@mui/icons-material";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";

import Table from "../../components/Table";
import Modal from "../../components/Modal";
import Button from "../../components/Button";
import FilterInput from "../../components/Filter/FilterInput";
import EmptyState from "../../components/EmptyState";
import Pagination from "../../components/Pagination";
import SearchInput from "../../components/SearchInput";
import FilterLabel from "../../components/Filter/FilterLabel";
import { ROUTE_PATHS } from "../../utils/RoutesPaths";
import { DeleteBgIcon, EditBgIcon } from "../../utils/Icons";
import {
  renderTextEllipsis,
  setPageMainTitle,
  textContainOnlySpaces,
} from "../../utils/Helpers";
import messages from "../../assets/locale/messages";
import {
  deleteServiceRequest,
  getServicesRequest,
} from "../../store/Services/actions";
import { getSpecialtiesRequest } from "../../store/Specialties/actions";
import { getCategoriesRequest } from "../../store/Categories/actions";
import "./Services.scss";

const Services = () => {
  const history = useHistory();
  const dispatch = useDispatch();

  const lang = useSelector((state) => state.locale.lang);
  const isLoading = useSelector((state) => state.loader);
  const meta = useSelector((state) => state.services.meta);
  const list = useSelector((state) => state.services.list);
  const specialtiesList = useSelector((state) => state.specialties.list);
  const categoriesList = useSelector((state) => state.categories.list);

  const [page, setPage] = useState(1);
  const [searchValue, setSearchValue] = useState("");
  const [categoriesOptions, setCategoriesOptions] = useState([]);
  const [specialtiesOptions, setSpecialtiesOptions] = useState([]);
  const [filterSpecialtyValue, setFilterSpecialtyValue] = useState(null);
  const [filterCategoryValue, setFilterCategoryValue] = useState(null);
  const [filterServiceTypeValue, setFilterServiceTypeValue] = useState(null);
  const [serviceTypeOptions, setServiceTypeOptions] = useState([]);
  const [openDeleteConfirm, setOpenDeleteConfirm] = useState(false);
  const [deleteServicesId, setdeleteServicesId] = useState(null);

  const { shared, services, pagesTitles } = messages[lang];
  useEffect(() => {
    setPageMainTitle("servicesManagement");
    getSpecialtiesLookup();
    getCategoriesLookup();
  }, []);

  useEffect(() => {
    getServices(
      page,
      searchValue,
      filterSpecialtyValue,
      filterCategoryValue,
      filterServiceTypeValue?.value
    );
  }, [page]);

  useEffect(() => {
    if (
      (searchValue.length >= 3 || searchValue.length === 0) &&
      !textContainOnlySpaces(searchValue)
    ) {
      setPage(1);
      getServices(
        1,
        searchValue,
        filterSpecialtyValue,
        filterCategoryValue,
        filterServiceTypeValue?.value
      );
    }
  }, [searchValue]);

  useEffect(() => {
    setPage(1);
    getServices(
      1,
      searchValue,
      filterSpecialtyValue,
      filterCategoryValue,
      filterServiceTypeValue?.value
    );
  }, [filterSpecialtyValue]);

  useEffect(() => {
    setPage(1);
    getServices(
      1,
      searchValue,
      filterSpecialtyValue,
      filterCategoryValue,
      filterServiceTypeValue?.value
    );
  }, [filterCategoryValue]);

  useEffect(() => {
    setPage(1);
    getServices(
      1,
      searchValue,
      filterSpecialtyValue,
      filterCategoryValue,
      filterServiceTypeValue?.value
    );
  }, [filterServiceTypeValue]);

  // To update filters options on language change
  useEffect(() => {
    setCategoriesOptions(
      categoriesList?.map((option) => ({
        id: option.id,
        label: lang === "en" ? option.title_en : option.title_ar,
      }))
    );
    setSpecialtiesOptions(
      specialtiesList?.map((option) => ({
        id: option.id,
        label: lang === "en" ? option.title_en : option.title_ar,
      }))
    );
  }, [categoriesList, specialtiesList, lang]);

  useEffect(() => {
    setServiceTypeOptions([
      {
        id: "single_service",
        label: services.list.singleService,
        value: "single_service",
      },
      {
        id: "multi_session",
        label: services.list.multiSessions,
        value: "multi_session",
      },
    ]);
  }, [lang, services.list.singleService, services.list.multiSessions]);

  // To update filters value on language change
  useEffect(() => {
    if (filterCategoryValue)
      setFilterCategoryValue(
        categoriesOptions.find((option) => option.id === filterCategoryValue.id)
      );
    if (filterSpecialtyValue)
      setFilterSpecialtyValue(
        specialtiesOptions.find(
          (option) => option.id === filterSpecialtyValue.id
        )
      );
    if (filterServiceTypeValue)
      setFilterServiceTypeValue(
        serviceTypeOptions.find(
          (option) => option.id === filterServiceTypeValue.id
        )
      );
  }, [lang, categoriesOptions, specialtiesOptions, serviceTypeOptions]);

  // Refetch services when language changes to update provider names
  useEffect(() => {
    getServices(
      page,
      searchValue,
      filterSpecialtyValue,
      filterCategoryValue,
      filterServiceTypeValue?.value
    );
  }, [lang]);

  const getServices = (pageNo, search, specialty, category, serviceType) => {
    const requestParams = {
      page: pageNo,
      items: 10,
      title: search,
      specialty_id: specialty?.id,
      category_id: category?.id,
      status: "accepted",
    };

    if (serviceType) {
      requestParams.service_type = serviceType;
    }

    dispatch(getServicesRequest(requestParams));
  };

  const getSpecialtiesLookup = () => {
    dispatch(
      getSpecialtiesRequest({
        page: -1,
      })
    );
  };
  const getCategoriesLookup = () => {
    dispatch(
      getCategoriesRequest({
        page: -1,
      })
    );
  };

  const handleEditServices = (id) => {
    history.push(ROUTE_PATHS.editServices.replace(":id", id));
  };
  const handleDeleteClick = (id) => {
    setdeleteServicesId(id);
    setOpenDeleteConfirm(true);
  };
  const handleDeleteServices = () => {
    dispatch(
      deleteServiceRequest({
        id: deleteServicesId,
        currentPage: meta.currentPage,
      })
    );
    setOpenDeleteConfirm(false);
  };
  const handleCloseDeleteConfirmModal = () => {
    setOpenDeleteConfirm(false);
  };
  const renderHeaderSection = (showActions) => (
    <>
      <div className="services-header d-flex justify-content-between mb-4 gap-2">
        <div className="title-search d-flex flex-column flex-md-row">
          <Typography
            variant="h4"
            sx={{ flexGrow: 1 }}
            className={`page-title ${lang === "en" ? "me-2" : "ms-2"}`}
          >
            {pagesTitles.services}
          </Typography>
          {showActions && (
            <SearchInput
              placeholder={services.list.search}
              name="search"
              value={searchValue}
              onChange={(value) => {
                setSearchValue(value);
              }}
            />
          )}
        </div>
        {showActions && (
          <div className="add-btn-wrapper">
            <Button
              label={
                <span className="d-flex align-items-center">
                  <Add fontSize="large" />
                  <span className="ps-1 ">
                    {services.list.CreateServicesButton}
                  </span>
                </span>
              }
              labelClass="fsize-16 text-white pe-2"
              onClick={() => {
                history.push(ROUTE_PATHS.addServices);
              }}
            />
          </div>
        )}
      </div>
      {showActions && (
        <div className="filter-label-wrapper d-flex align-items-center mb-4 w-50 gap-2 flex-wrap flex-md-nowrap">
          <FilterLabel />
          <FilterInput
            placeholder={
              services.list.filter.filtersPlaceHolders.filterByCategory
            }
            options={categoriesOptions}
            value={filterCategoryValue}
            name="Category Filter"
            onChange={(value) => {
              setFilterCategoryValue(value);
            }}
            filterWrapperClass="my-3 my-md-0 w-100"
          />
          <FilterInput
            placeholder={
              services.list.filter.filtersPlaceHolders.filterBySpecialty
            }
            options={specialtiesOptions}
            value={filterSpecialtyValue}
            name="specialty Filter"
            onChange={(value) => {
              setFilterSpecialtyValue(value);
            }}
            filterWrapperClass="my-3 my-md-0 w-100"
          />
          <FilterInput
            placeholder={
              services.list.filter.filtersPlaceHolders.filterByServiceType
            }
            options={serviceTypeOptions}
            value={filterServiceTypeValue}
            name="Service Type Filter"
            onChange={(value) => {
              setFilterServiceTypeValue(value);
            }}
            filterWrapperClass="my-3 my-md-0 w-100"
          />
        </div>
      )}
    </>
  );
  const ServicesList = () => {
    if (
      !isLoading &&
      list?.length === 0 &&
      !searchValue &&
      !filterSpecialtyValue &&
      !filterCategoryValue
    ) {
      return (
        <>
          {renderHeaderSection(false)}
          <div className="bg-white rounded-4 servicess-page d-flex justify-content-center align-items-center flex-column m-4">
            <EmptyState
              subTitle={shared.noData}
              description={services.list.noDataDescription}
              ButtonLabel={services.list.CreateServicesButton}
              handleBtnAction={() => {
                history.push(ROUTE_PATHS.addServices);
              }}
            />
          </div>
        </>
      );
    } else if (
      !isLoading &&
      (searchValue || filterSpecialtyValue || filterCategoryValue) &&
      list?.length === 0
    ) {
      return (
        <>
          {renderHeaderSection(true)}
          <div className="bg-white rounded-4 services-page d-flex justify-content-center align-items-center flex-column m-4">
            <EmptyState subTitle={shared.noSearch} showActionButton={false} />
          </div>
        </>
      );
    } else {
      return (
        <>
          <div>
            {renderHeaderSection(true)}
            <div className="bg-white rounded-4 my-3 services-table-list">
              <Table
                className="rounded-4"
                headlines={[
                  services.list.enTitle,
                  services.list.arTitle,
                  services.list.specialty,
                  services.list.category,
                  services.list.serviceType,
                ]}
                hasActions={true}
                rows={list?.map((row, i) => (
                  <TableRow key={i}>
                    <TableCell className="start font-regular text-dark-blue fsize-14 text-nowrap">
                      {renderTextEllipsis(row.title_en || "-")}
                    </TableCell>
                    <TableCell className="font-regular text-dark-blue fsize-14 text-nowrap">
                      {renderTextEllipsis(row.title_ar || "-")}
                    </TableCell>
                    <TableCell className="font-regular text-dark-blue fsize-14 text-nowrap">
                      {row.specialty
                        ? lang === "en"
                          ? renderTextEllipsis(row.specialty.title_en)
                          : renderTextEllipsis(row.specialty.title_ar)
                        : "-"}
                    </TableCell>
                    <TableCell className="font-regular text-dark-blue fsize-14 text-nowrap">
                      {row.categories.length > 0
                        ? row.categories.map((category, i, array) => (
                            <span key={i}>
                              {renderTextEllipsis(
                                lang === "en"
                                  ? category.name_en
                                  : category.name_ar
                              )}
                              {i !== array.length - 1 && " , "}
                            </span>
                          ))
                        : "-"}
                    </TableCell>
                    <TableCell className="font-regular text-dark-blue fsize-14 text-nowrap">
                      {(() => {
                        const isMulti =
                          row.allowMultiSession || row.service_type === "multi_session";
                        const serviceTypeLabel = isMulti
                          ? services.list.multiSessions
                          : services.list.singleService;

                        if (!isMulti) {
                          return (
                            <span className="text-nowrap">
                              {serviceTypeLabel}
                            </span>
                          );
                        }

                        const providerName =
                          lang === "en" ? row.creator_name_en : row.creator_name_ar;
                        const infoText = providerName
                          ? `${services.list.multiSessionsInfo} ${providerName}`.trim()
                          : services.list.multiSessionsInfoByAdmin;
                        return (
                          <div className="d-flex align-items-center gap-1 text-nowrap">
                            <span>{serviceTypeLabel}</span>
                            <Tooltip title={infoText} arrow placement="top">
                              <span className={`text-bg-primary-mid fsize-14 font-regular ${lang === "en" ? "ms-2" : "me-2"}`}>                                <InfoOutlinedIcon
                                  fontSize="small"
                                  sx={{ color: "text-bg-primary-mid" }}
                                />
                              </span>
                            </Tooltip>
                          </div>
                        );
                      })()}
                    </TableCell>
                    <TableCell className="font-regular text-dark-blue fsize-14">
                      <div className="table-icons-container gap-4">
                        <EditBgIcon
                          className="pointer"
                          onClick={() => handleEditServices(row.id)}
                        />
                        <DeleteBgIcon
                          className="pointer"
                          onClick={() => handleDeleteClick(row.id)}
                        />
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              />
              <div className="d-flex justify-content-center py-3">
                {meta?.pages > 1 && (
                  <Pagination
                    count={meta?.pages}
                    page={meta?.currentPage || page}
                    handleChange={(event, page) => {
                      setPage(page);
                    }}
                    defaultPage={1}
                  />
                )}
              </div>
            </div>
          </div>
          <Modal
            open={openDeleteConfirm}
            handleClose={handleCloseDeleteConfirmModal}
            title={services.deleteService.title}
            contentClass="mb-5 pb-2 text-primary"
            content={
              <span>{services.deleteService.deleteConfirmationMsg}</span>
            }
            confirmBtnTxt={shared.delete}
            maxWidth="xs"
            cancelBtnTxt={shared.cancel}
            isContentText={false}
            handleConfirm={handleDeleteServices}
            handleCancel={handleCloseDeleteConfirmModal}
            modalClass="delete-services-modal"
            actionsClass="container px-4"
            confirmBtnClass="btn-secondary fsize-14 text-white py-2 "
            cancelBtnClass="fsize-14 py-2"
          />
        </>
      );
    }
  };

  return <div className="services-container">{ServicesList()}</div>;
};

export default Services;
